/**
 * Plays the video it wraps once enough of it is on screen, then stops watching.
 *
 * Paired with `preload="none"` so nothing is fetched for a video the visitor
 * never scrolls to. Left alone entirely under prefers-reduced-motion, where the
 * poster frame stands in.
 *
 * Ported from the inline IIFE in ../foamico-main/index.html and generalised:
 * the original targeted one specific video, this wraps any of them.
 *
 * Guarded: the Sleep Test and the Explore by Features cards both load it, and
 * every `<script src>` executes on each occurrence — unguarded, the second run
 * redeclared the constant and redefined the element, and threw.
 */
(() => {
  if (customElements.get('play-in-view')) return;

  const VISIBLE_ENOUGH = 0.35;

  class PlayInView extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (!('IntersectionObserver' in window)) {
        this.video.play().catch(() => {});
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this.video.play().catch(() => {
              /* autoplay can be refused; the poster stays */
            });
            this.observer.unobserve(entry.target);
          }
        },
        { threshold: VISIBLE_ENOUGH }
      );

      this.observer.observe(this.video);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  customElements.define('play-in-view', PlayInView);
})();
